// ════════════════════════════════════════════════════════════════════════════
//  ManDana — projects
//  ---------------------------------------------------------------------------
//  A project is an effort inside one domain: a clear target, and a brick in what
//  that part of a life is being built towards. Health holds Bodybuilding and
//  Nutrition; Career holds ManDana; Spirit holds Prayers.
//
//  A PROJECT HAS A BODY BUT NOT A FILING CABINET. Its own writing — the vision,
//  the why, the milestones — lives on it, in the same editor everything else in
//  this app is written in. Its RECORDS do not: they stay in the days they happened
//  on and point back with `pid`. So joining a record to a project moves nothing,
//  and deleting a project destroys nothing. That is the whole reason the day
//  stays the spine of this app rather than acquiring a rival.
//
//  Storage mirrors md-board.ts exactly, because the storage unit has to be the
//  sync unit: one index document listing the projects, one document per project
//  holding its writing.
//
//  ── the metric ────────────────────────────────────────────────────────────
//  TIME SPENT. Chosen, not imposed: this paragraph used to read as an app-wide
//  prohibition on percentages, completion rates and streaks, and that rule was
//  struck from the doctrine on 2026-08-18. Nothing here forbids a percentage
//  any more, and a surface with a reason for one may draw it.
//
//  What survives the amendment is the reason THIS number is the one on a
//  project's card. A number that can fall is a number that will eventually
//  greet somebody with a verdict on a hard week; time given to something only
//  accumulates, so there is no bad month, no line to drop below, and nothing to
//  be told about. That is a judgement about a card somebody opens on a bad day,
//  not a law about arithmetic.
//
//  Two rules keep it true rather than merely kind:
//    • only time that has ALREADY PASSED counts. Scheduling six hours next week
//      must not read as six hours given today, or the number becomes a measure
//      of intention and stops being evidence of anything.
//    • nothing is ever subtracted. Deleting a record stops it counting; it does
//      not create a debt, and there is no correction to explain.
//
//  ── the dates: a start, a target, and what may be drawn with them ─────────
//  The paragraph above rejects "a distance from a target" by name, so a date
//  needs an argument rather than an exception.
//
//  What that paragraph declines is a NUMBER THAT FALLS. "12 days left" falls;
//  it counts down to a moment where it will have run out and said so. A DATE
//  does not fall — "19 Sep" is a fact about a plan somebody made, and it is the
//  same fact on the day it is set, the day before, and the week after. Neither
//  does a SPAN: two chosen days with work between them.
//
//  A project's dates are drawn however the screen reads best — a day, a span,
//  a bar, a marker on an axis. This paragraph used to forbid a shape in order
//  to protect a feeling, and it cost the app its timeline: the engine has held
//  a target since the day it was written and no list has ever shown one, because
//  every way of showing it was ruled out here. Shapes are a design question now,
//  answered by what makes the work legible, and "the metric" above no longer
//  rules on them. What it still rules is the METRIC — the number this app says
//  a project IS — and that remains time given, which only accumulates.
//
//  BOTH DATES ARE OPTIONAL and both are a normal absence. A project created
//  before either field existed is never backfilled, because today + 40 is a
//  date nobody chose. `start` in particular has no default at all: a target is
//  the plan's forty days, but when something began is a fact only the user has.
//
//  BOTH ARE DAY KEYS, never offsets. An offset means a different day tomorrow
//  and a different day again on a device in another timezone; md-theme.jsx
//  says the same thing about everything else this app files.
//
//  A SPAN MOVES AS ONE. mdProjectSetSpan validates both ends and writes once,
//  because a drag on a timeline is one gesture: two sequential setters would be
//  two undo steps, two sync documents, and a window in which a refused second
//  write leaves a start sitting after its target.
//
//  ── the built-in ──────────────────────────────────────────────────────────
//  Prayers ships with the app, because prayer is the axis and it would be odd
//  for the one thing the whole day is organised around to be something you had
//  to remember to create.
//
//  It is deliberately NOT STORED. A built-in that lives in the index could be
//  renamed by an old client, dropped by a bad merge, or deleted by a stray tap,
//  and then it is not built in — it is just a project somebody happens to have.
//  It is injected on read instead, so it cannot be lost and its definition can
//  change in a release without a migration.
//
//  Its items are not records. Prayers are generated per day from the sun and only
//  their done marks persist, so there is no duration in storage to add up. Its
//  time is therefore counted from those marks at the app's own nominal prayer
//  window — a WINDOW, not a stopwatch, and the UI is required to say so.
//
//  ── where a built-in keeps what the user set on it ────────────────────────
//  Not being in the index is what makes the built-in unloseable, and it is also
//  what leaves it nowhere to put a field somebody chose. Its target used to be
//  refused outright for that reason, dressed as ontology — "prayer has no finish
//  line" — and the refusal outlived the law behind it.
//
//  It goes on the project's OWN HOME DOCUMENT, `md:project:<id>`, which already
//  exists for every project, already carries the writing, and already syncs
//  under a key the server accepts. So a built-in's user-set state travels by the
//  route its writing travels, needs no second document and no server change, and
//  still cannot put a row in the index for readIndex to refuse.
//
//  Two consequences the code below has to honour. The home document must travel
//  when it holds ONLY these fields and no writing, or a target set on a silent
//  project stays on one device for ever. And a CLEARED field is stored as an
//  explicit null rather than deleted, because an absent key cannot be told apart
//  from a document written by a client that never heard of the field.
// ════════════════════════════════════════════════════════════════════════════

/** A row in the projects list. `closed` is a quiet end, not a deletion.
    `target` is a `YYYY-MM-DD` day key and is optional — see the header.

    `status` is where a project stands, and it is the USER'S word. Nothing in
    this app derives it, promotes it or infers it from a count: the four values
    exist so somebody can say where they are, and every existing project carries
    `undefined`, which reads as "not saying". */
interface MdProject {
  id: string;
  name: string;
  domainId: string;
  pursuitId?: string;
  at: number;
  start?: string;
  target?: string;
  status?: MdProjectStatus;
  priority?: MdProjectPriority;
  closed?: boolean;
  builtin?: boolean;
}

/* A status id, resolved against the workflow the project reads through — NOT a
   word this file knows. It kept a union of four until Phase 5, which stopped
   being possible the moment a domain could be given a vocabulary of its own.
   The four are still ids in the default workflow, so every project document
   ever written keeps its stored word and nothing migrates. */
type MdProjectStatus = string;

/** How hard a project pushes, and it is the USER'S WORD in exactly the sense
    `status` is: nothing here derives it, promotes it, or reads it off a target
    drawing near. The four are a LADDER somebody reads, so the order is the
    order — filing High under H beside Low is how a priority column stops being
    readable — and the absence of one is a real answer, not a missing value. */
type MdProjectPriority = 'urgent' | 'high' | 'medium' | 'low';

/** Why a change was refused. Every setter below returns a bare boolean, and
    `false` has always meant five different things at once: an agent asked, a
    built-in was asked something only a stored row can answer, the project is
    gone, the argument was not a real one, or the device is out of room.

    That was survivable while the only caller was a picker inside one project —
    the picker could not offer an impossible choice. A BOARD CAN: a card is
    dragged at whatever column is under the pointer, and "This change could not
    be saved" in front of somebody whose project simply follows its pursuit's
    domain sends them to look for a bug that is not there. */
/* `admin` replaced `builtin`, and the difference is the whole of G5: the old
   word named a SHAPE — a built-in had no row, so nobody could change it — and
   the new one names a PERMISSION, which is the honest answer now that the row
   exists and somebody could. `builtin` is kept in the union because a refusal
   can still arrive from an older build through a synced document, and a word
   the type does not admit is a word the surface cannot render. */
type MdProjectRefusal = 'agent' | 'admin' | 'builtin' | 'missing' | 'invalid' | 'has_pursuit'
  | 'no_project' | 'storage_full';

type MdProjectResult = { ok: true } | { ok: false; reason: MdProjectRefusal };

/** What one project's card shows. Every field only ever grows. */
interface MdProjectStats {
  minutes: number;
  records: number;
  boards: number;
}

/** A commitment's number, and the sentence that says what it is a number OF.
 *  `measures` travels with `days` on purpose: a bare "12/40" under a title
 *  about jamāʿah would read as a claim this app cannot make.
 */
interface MdProjectReading {
  measures: string;
  days: number;
  of: number;
}

(function (g: any) {
  'use strict';

  const K_INDEX = 'md:projects';          // the list — synced, one document
  const K_PROJECT = 'md:project:';        // + id — one document per project's writing

  const NAME_MAX = 60;

  /* The five, by the ids md-theme.jsx gives them. Named here rather than
     imported because md-theme is a React module that loads much later, and this
     file must answer questions before it exists. */
  const MD_PRAYER_IDS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

  /* What one prayer is counted as. md-theme draws a prayer as a ~30 minute block
     (see mdDayItems), and this is that same window. It is not a measurement of
     how long anybody stood — nothing in this app knows that, and nothing should
     pretend to. */
  const MD_PRAYER_NOMINAL_MIN = 30;

  /* The statuses THIS project may stand in: the ones its resolved workflow
     holds. There is deliberately no module-level list any more — "what statuses
     exist" stopped being a fact about the app and became a question about one
     project, and two rows on the same screen can now answer it differently. */
  function mdProjectStatuses(id: unknown): any[] {
    if (!g.mdWorkflowFor) return [];
    return g.mdWorkflowFor('project', id).statuses;
  }

  /** Where a project stands, resolved — never null while a workflow exists,
      because absence IS a place now (the first Backlog status; see WF-2). */
  function mdProjectStatusOf(id: unknown): any {
    if (!g.mdWorkflowFor) return null;
    const project = mdProjectById(id);
    return g.mdWorkflowResolveStatus(g.mdWorkflowFor('project', id).id,
      project ? project.status : null);
  }

  /** Where THIS project would land in a spine category — the first status of
      that category in the workflow it reads through. The board asks per card
      (WF-13): one column named "In Progress" means a different id for a card
      on the Spiritual workflow than for one on the default. */
  function mdProjectStatusLanding(id: unknown, category: unknown): any {
    if (!g.mdWorkflowFor || !g.mdWorkflowCategoryLanding) return null;
    return g.mdWorkflowCategoryLanding(g.mdWorkflowFor('project', id).id, category);
  }

  function knownStatus(id: unknown, status: unknown): boolean {
    return mdProjectStatuses(id).some((row: any) => row.id === status);
  }

  const PRIORITIES: MdProjectPriority[] = ['urgent', 'high', 'medium', 'low'];

  /* ── what ships with the app ─────────────────────────────────────────────
     Prayers keeps its id and its place at the front: prayer minutes already
     file against `prayers` (see mdProjectStats below), and existing accounts
     hold records pointing at it. It gains only its pursuit.

     The five under IQAMA are a LADDER, and the statuses say where the ladder
     starts, not where anybody is on it. The jamāʿah and takbīr commitments sit
     in `backlog` for an honest reason: ManDana stores one boolean per prayer,
     with no time and no congregation signal, so it cannot yet see either of
     them. A user promotes one by hand when they decide to climb; no code path
     below writes a status without being asked. The app is a witness. */
  const PRAYERS: MdProject = {
    id: 'prayers', name: 'Prayers', domainId: 'e6', pursuitId: 'iqama', at: 0, builtin: true,
  };

  const BUILTINS: MdProject[] = [
    PRAYERS,
    { id: 'iqamaontime', name: '40 Days On Time Prayer',
      domainId: 'e6', pursuitId: 'iqama', at: 0, status: 'active', builtin: true },
    { id: 'iqamajamaah', name: '40 Days On Time Jamāʿah Prayer',
      domainId: 'e6', pursuitId: 'iqama', at: 0, status: 'backlog', builtin: true },
    { id: 'iqamatakbir', name: '40 Days Takbīrat al-Iḥrām Jamāʿah Prayer',
      domainId: 'e6', pursuitId: 'iqama', at: 0, status: 'backlog', builtin: true },
    { id: 'iqamaqiyam', name: 'Qiyām',
      domainId: 'e6', pursuitId: 'iqama', at: 0, status: 'parked', builtin: true },
    { id: 'iqamasunna', name: 'Sunna',
      domainId: 'e6', pursuitId: 'iqama', at: 0, status: 'parked', builtin: true },
  ];

  const BUILTIN_IDS = new Set(BUILTINS.map((row) => row.id));

  /** Is this a built-in being changed by a hand that may not? Five verbs ask —
      delete, rename, domain, pursuit, closed — because those change what the
      thing IS. Closing is on the list because it leaves the board the same way
      removal does; it also WIDENS, since before this nobody could close one. Status, priority, start and target never ask: where somebody
      stands on the IQAMA ladder is their own word. See md-admin-door.ts, which
      also explains why this is an intent boundary and not a security one. */
  const adminHeld = (id: unknown): boolean =>
    BUILTIN_IDS.has(id as string) && !(typeof g.mdAdminIs === 'function' && g.mdAdminIs());

  function builtinById(id: unknown): MdProject | null {
    return typeof id === 'string' && BUILTIN_IDS.has(id)
      ? BUILTINS.find((row) => row.id === id) || null : null;
  }

  /* ── what carries a reading ──────────────────────────────────────────────
     A commitment's number is a count of days on which something was true, and
     the only thing ManDana can see today is that all five prayers were marked:
     one boolean per prayer, no timestamp, no congregation signal. So exactly
     one commitment appears below, and the entry is keyed by WHAT IS MEASURED
     rather than by the project's title.

     The jamāʿah and takbīr commitments are absent deliberately, and they stay
     absent even if a user promotes one by hand. Borrowing the on-time count to
     fill their column would be the app claiming to have watched something it
     never asked about. A blank is the honest answer to a question nobody put. */
  const READINGS: Record<string, string> = {
    iqamaontime: 'Days all five prayers were marked',
  };

  /* The length of the commitment, which is not the same decision as how far a
     new project aims by default — they agree at 40 today by coincidence of the
     forty-day tradition, and changing one must not silently move the other. */
  const MD_READING_WINDOW_DAYS = 40;

  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

  /* How far out a new project aims by default. Named once so it is a decision
     somebody can argue with, rather than a 40 sitting in the middle of the
     creation path. */
  const MD_PROJECT_TARGET_DAYS = 40;

  /* Today plus n days, as a day key. Computed here rather than borrowed from
     md-theme's mdDayKey for the same reason MD_PRAYER_IDS is spelled out above:
     md-theme is a React module that loads much later, and this file has to be
     able to answer before it exists.

     Built by CONSTRUCTING a date rather than adding 86400000 × n, so a daylight
     saving boundary inside the window cannot land the target a day early. */
  function dayKeyIn(days: number): string {
    const t = new Date();
    const d = new Date(t.getFullYear(), t.getMonth(), t.getDate() + days);
    const p2 = (n: number) => (n < 10 ? '0' + n : String(n));
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  }

  /* Lowercase alphanumeric, like a board id and for the same reason: the id ends
     up inside a sync key, and server/sync.js matches keys against a pattern — an
     id with a colon or a dash in it is a project that silently never syncs. */
  function newId(): string {
    let s = '';
    while (s.length < 10) s += Math.random().toString(36).slice(2).replace(/[^a-z0-9]/g, '');
    return s.slice(0, 10);
  }

  function isRow(row: any): row is MdProject {
    return !!row && typeof row === 'object'
      && typeof row.id === 'string' && !!row.id
      && !BUILTIN_IDS.has(row.id)                    // never let a stored row shadow a built-in
      && typeof row.name === 'string'
      && typeof row.domainId === 'string';
  }

  function readIndex(): MdProject[] {
    const raw = g.mdRead(K_INDEX, []);
    return Array.isArray(raw) ? raw.filter(isRow) : [];
  }

  function writeIndex(rows: MdProject[]): boolean {
    return g.mdWrite(K_INDEX, rows);
  }

  /* ── the built-in's home document, as its index row ──────────────────────
     See the header. These are the fields that may be set on a project that has
     no row to set them in; everything else about a built-in is defined in code.
     Adding to this list is the whole extension point — nothing else below names
     a field by hand.

     `name` IS NOT LIKE THE OTHER FOUR, and it is first to make that visible.
     The four below are the user's own word about where they stand and are set
     by any hand. A name is the app's, and mdProjectTryRename asks the admin door
     before it writes one — see md-admin-door.ts. It lives on this list because
     the OVERLAY mechanism is identical: a shipped default in code, a document
     that may lay something over it, and an explicit null meaning "nothing here",
     which for a name reads as the shipped name coming back. */
  const BUILTIN_FIELDS = ['name', 'deleted', 'closed', 'domainId', 'pursuitId',
    'start', 'target', 'status', 'priority'];

  /* Everything the home document holds ON A PERSON'S BEHALF, which is a longer
     list than the built-in's three. `records` — where each of this project's
     records stands (WF-6) — belongs to every project, built-in or not, and is
     nobody's index-row field: materialiseBuiltin walks BUILTIN_FIELDS and so
     never tries to lay a map of uids over a project row.

     Being on THIS list is what makes a field survive its project's paragraph
     being emptied, travel on a document with no writing beside it, and merge
     rather than be replaced when a pull arrives. One list, three behaviours,
     and no fourth place to remember. */
  const HOME_FIELDS = [...BUILTIN_FIELDS, 'records'];

  function homeDoc(id: string): Record<string, any> | null {
    const raw = g.mdRead(K_PROJECT + id, null);
    return raw && typeof raw === 'object' ? raw : null;
  }

  /** Only the user-set fields of a home document, or null if it holds none. An
      explicit null is a CLEARED field and counts as held — see the header. */
  function homeFields(home: Record<string, any> | null): Record<string, any> | null {
    if (!home) return null;
    const out: Record<string, any> = {};
    let any = false;
    for (const field of HOME_FIELDS) {
      if (!(field in home)) continue;
      out[field] = home[field];
      any = true;
    }
    return any ? out : null;
  }

  /** The built-in as the user has it: the definition from code, with whatever
      they set on its home document laid over the top.

      An explicit null is the user saying nothing here — see the header — and
      what shows then is whatever the app ships. For a target that is nothing,
      because no built-in is born with one; for a status it is the shipped word,
      so clearing a promotion returns the commitment to the rung it started on
      rather than blanking it. One rule, two honest outcomes. */
  function materialiseBuiltin(row: MdProject): MdProject {
    const fields = homeFields(homeDoc(row.id));
    if (!fields) return row;
    const out: Record<string, any> = { ...row };
    for (const field of BUILTIN_FIELDS) {
      if (!(field in fields)) continue;
      if (fields[field] == null) { if (!(field in row)) delete out[field]; }
      else out[field] = fields[field];
    }
    return out as MdProject;
  }

  function builtinSet(id: string, field: string, value: unknown): boolean {
    const home = homeDoc(id) || {};
    return g.mdWrite(K_PROJECT + id, { ...home, [field]: value, at: Date.now() });
  }

  /** Give every built-in a document to be found by, and return how many had to
      be written — so a settled account can PROVE a second boot writes nothing.

      IT WRITES THE HOME DOCUMENT AND NEVER THE INDEX. `isRow` above rejects any
      stored row carrying a built-in id, so an index materialised here would be
      stripped by any device still running an older bundle and written back
      empty, and the two devices would fight over it forever. A home document has
      no such problem: older bundles already carry these (that is how a built-in's
      target reaches a second device) and already keep fields they do not know.

      THE CLOCK IS DELIBERATELY NOT `Date.now()`. mdProjectApplyDoc lets a newer
      local document win outright, so stamping this with the current time would
      make the app's own statement about what it ships outrank a real edit
      arriving from another device. An existing document keeps whatever clock it
      had; a new one starts at 0, which loses every merge it is ever in. That is
      correct: nothing here is a person's writing. */
  function mdProjectMaterialiseBuiltins(): number {
    let wrote = 0;
    for (const row of BUILTINS) {
      const home = homeDoc(row.id);
      if (home && 'name' in home) continue;      // already named, or admin-renamed
      const at = home && Number.isFinite(home.at) ? home.at : 0;
      if (g.mdWrite(K_PROJECT + row.id, { ...(home || {}), name: row.name, at })) wrote += 1;
    }
    return wrote;
  }

  /** Has the admin removed this built-in? A built-in cannot be taken out of an
      index it was never in, so "gone" is a field on its home document rather
      than an absence. Which is the better shape anyway: the definition in code
      never moved, so this is undoable where a real delete is not. */
  function builtinRemoved(id: string): boolean {
    const home = homeDoc(id);
    return !!(home && home.deleted === true);
  }

  /** The built-ins the admin has removed, so they can be found and put back. */
  function mdProjectRemovedBuiltins(): string[] {
    return BUILTINS.filter((row) => builtinRemoved(row.id)).map((row) => row.id);
  }

  /** Every project, the built-in first. `{ open: true }` leaves out closed ones. */
  function materialise(row: MdProject): MdProject {
    const pursuit = row.pursuitId && g.mdPursuitById ? g.mdPursuitById(row.pursuitId) : null;
    return pursuit ? { ...row, domainId: pursuit.domainId } : row;
  }

  function mdProjectIndex(opts?: { open?: boolean }): MdProject[] {
    const rows = [...BUILTINS.filter((row) => !builtinRemoved(row.id)).map(materialiseBuiltin),
      ...readIndex().map(materialise)];
    return opts && opts.open ? rows.filter((p) => !p.closed) : rows;
  }

  /* ── the lookup, keyed ───────────────────────────────────────────────────
     Every row of the Main DB walks its one upward pointer to name its Parent,
     and the first step is this function. It used to read the index document
     out of storage and JSON.parse it on every call, then scan the result
     linearly for one id — once per record, over a document that had not moved
     since the first row. On the 5,000-record account that was 4,670 calls and
     154 ms, twenty-nine per cent of composing the catalogue.

     So the read path gets an id-keyed map, retired by mdStorageRevision() like
     every other whole-device read here. The WRITE path deliberately keeps
     reading its own copy: its callers parse the rows, mutate one in place and
     write the array back, and that write can fail on the quota wall this app
     exists to be honest about. A shared array would leave a rename sitting in
     memory that never reached storage — the silent write failure md-storage.js
     was written to end. */
  let byId: { revision: number; map: Map<string, MdProject> } | null = null;
  function indexById(): Map<string, MdProject> {
    const revision = typeof g.mdStorageRevision === 'function' ? g.mdStorageRevision() : null;
    if (byId && revision !== null && byId.revision === revision) return byId.map;
    const map = new Map<string, MdProject>();
    for (const row of readIndex()) if (!map.has(row.id)) map.set(row.id, row);
    if (revision !== null) byId = { revision, map };
    return map;
  }

  function mdProjectById(id: unknown): MdProject | null {
    if (typeof id !== 'string' || !id) return null;
    const builtin = builtinById(id);
    if (builtin) return builtinRemoved(builtin.id) ? null : materialiseBuiltin(builtin);
    const row = indexById().get(id);
    /* Copied on the way out: materialise() hands the row straight back when it
       belongs to no pursuit, and that row is the map's own. */
    return row ? materialise({ ...row }) : null;
  }

  /** What a project may be BEGUN holding, beyond its name and its home.

      The creation sheet asks seven questions at once (B-1), and the alternative
      to taking them together is four sequential setters: create, then status,
      then priority, then the span. That is four writes against the quota wall
      this file exists to be honest about, and the third one failing leaves a
      project holding some of what was asked for and no way to say which part
      went missing. One validated write, or none. */
  interface MdProjectSeed {
    status?: unknown;
    priority?: unknown;
    start?: unknown;
    target?: unknown;
  }

  /** Create one inside a domain. Null when it would not be a real project.

      `seed` is optional and every field of it is checked by the same rule its
      own setter applies. ONE BAD ANSWER REFUSES THE WHOLE BEGINNING rather
      than being dropped on the way in: a sheet that quietly discards a date
      somebody typed is worse than one that says the date was not a day. */
  function mdProjectCreate(name: unknown, domainId: unknown, pursuitId?: unknown,
    seed?: MdProjectSeed): MdProject | null {
    const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
    if (!clean) return null;                          // an unnamed effort is not one
    const pursuit = pursuitId && g.mdPursuitById ? g.mdPursuitById(pursuitId) : null;
    const ownDomain = pursuit ? pursuit.domainId : domainId;
    if (!g.mdDomainById(ownDomain)) return null;      // and it must live somewhere

    const given = seed || {};
    /* `undefined` is "nothing was said about this", which is not the same as an
       explicit null — a sheet that leaves Target blank means no target, and the
       forty-day default is only for a caller that never mentioned it at all. */
    const said = (field: keyof MdProjectSeed) => field in given && given[field] !== undefined;
    const emptied = (value: unknown) => value == null || value === '';

    /* THE VOCABULARY IS THE WORKFLOW'S — and it is the one this project is
       about to speak, not the account's default. A project being born has no
       assignment of its own, but it is not therefore in the default: it reads
       through its PURSUIT'S workflow, or its domain's, from its very first
       instant (mdWorkflowSourceFor). The fixed five this used to be checked
       against are gone (WF-1), and for a while the default stood in for the
       cascade because the only door that seeded a status was a sheet offering
       the default's own words. That stopped being true the moment a beginning
       could be made inside a track (CS-8): the word was real where the project
       was going, this refused it, and `create` returned null — so the project
       simply did not happen, for a reason nothing on screen could name.

       Still refused rather than dropped: a caller naming a status that exists
       in NO vocabulary this project can reach has misunderstood something, and
       creating it anyway would put the project somewhere nobody asked for. */
    let status: MdProjectStatus | undefined;
    if (said('status') && !emptied(given.status)) {
      const flow = g.mdWorkflowSourceFor
        ? g.mdWorkflowSourceFor(pursuit ? 'pursuit' : 'domain',
          pursuit ? (pursuitId as string) : (ownDomain as string)).workflowId
        : 'default';
      if (typeof given.status !== 'string' || !g.mdWorkflowStatusById
        || !g.mdWorkflowStatusById(flow, given.status)) return null;
      status = given.status as MdProjectStatus;
    }
    let priority: MdProjectPriority | undefined;
    if (said('priority') && !emptied(given.priority)) {
      if (typeof given.priority !== 'string'
        || PRIORITIES.indexOf(given.priority as MdProjectPriority) === -1) return null;
      priority = given.priority as MdProjectPriority;
    }
    let start: string | undefined;
    if (said('start') && !emptied(given.start)) {
      if (!realDay(given.start)) return null;
      start = given.start;
    }
    /* Absent means the plan's forty days; an explicit null means a project with
       no finish date, which is a normal thing for one to be. */
    let target: string | undefined = said('target') ? undefined : dayKeyIn(MD_PROJECT_TARGET_DAYS);
    if (said('target') && !emptied(given.target)) {
      if (!realDay(given.target)) return null;
      target = given.target;
    }
    /* A span cannot be born inverted, for mdProjectSetSpan's own reason: there
       is no moment at which a start sitting after its target is a state the
       user asked for. */
    if (start && target && start > target) return null;

    const row: MdProject = { id: newId(), name: clean, domainId: ownDomain as string,
      ...(pursuit ? { pursuitId: pursuit.id } : {}),
      at: Date.now(),
      ...(status ? { status } : {}),
      ...(priority ? { priority } : {}),
      ...(start ? { start } : {}),
      ...(target ? { target } : {}) };
    if (!writeIndex([...readIndex(), row])) return null;
    return row;
  }

  /* The shape of a day and the existence of one are different questions, and
     DAY_RE only ever asked the first. Every other use of it in this file matches
     keys the app wrote itself, where shape is the whole story; a target comes
     from a person, where it is not. February 31st sorts quite happily between
     the 30th and March 1st, which is why an unreachable target sat in the index
     for as long as it did looking exactly like a real one.

     Date.UTC is the arbiter rather than a table of month lengths — it already
     knows every leap rule there is, and rolls an impossible day forward, so a
     value that does not survive the round trip was never a day. Twin of realDay
     in md-database.ts; the two files share no runtime, only the rule. */
  function realDay(value: unknown): value is string {
    if (typeof value !== 'string' || !DAY_RE.test(value)) return false;
    const year = Number(value.slice(0, 4)), month = Number(value.slice(5, 7)), day = Number(value.slice(8, 10));
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1
      && probe.getUTCDate() === day;
  }

  /** How hard this project pushes, or null for nobody having said.

      A word this build does not know reads as NULL rather than as itself. The
      argument is realDay's, one field over: a row written by a client that has
      a fifth rung must not put a project in a column this one cannot draw, and
      an absence it can draw is the honest way to show a value it cannot. */
  function mdProjectPriority(row: unknown): MdProjectPriority | null {
    const value = row && typeof row === 'object' ? (row as { priority?: unknown }).priority : null;
    return typeof value === 'string' && PRIORITIES.indexOf(value as MdProjectPriority) !== -1
      ? value as MdProjectPriority : null;
  }

  /** When this project is aiming to be done — a day key, or null for no target. */
  function mdProjectTarget(row: unknown): string | null {
    const t = row && typeof row === 'object' ? (row as { target?: unknown }).target : null;
    return realDay(t) ? t : null;
  }

  /** Move the target, or clear it with null. Refuses anything that is not a day,
      because a target that cannot be looked up in a calendar is not one.

      The built-in may carry one too. A target is a DATE somebody chose — the
      header argues at length that this is why it is allowed to exist at all —
      and a date is not a number that falls at anybody, so there was never a
      reason beyond the retired gamification law to keep it off Prayers. Its
      target lands on the home document rather than the index; see the header. */
  function mdProjectSetTarget(id: unknown, day: unknown): boolean {
    const clear = day == null || day === '';
    if (!clear && !realDay(day)) return false;
    if (builtinById(id)) return builtinSet(id as string, 'target', clear ? null : day);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (clear) { delete row.target; return writeIndex(rows); }
    row.target = day;
    return writeIndex(rows);
  }

  /** When this project began, or is planned to — a day key, or null for none.
      Unlike a target there is no default: nothing invents a beginning. */
  function mdProjectStart(row: unknown): string | null {
    const s = row && typeof row === 'object' ? (row as { start?: unknown }).start : null;
    return realDay(s) ? s : null;
  }

  /** Move the start, or clear it with null. The twin of mdProjectSetTarget in
      every respect, including where a built-in's lands. */
  function mdProjectSetStart(id: unknown, day: unknown): boolean {
    const clear = day == null || day === '';
    if (!clear && !realDay(day)) return false;
    if (builtinById(id)) return builtinSet(id as string, 'start', clear ? null : day);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (clear) { delete row.start; return writeIndex(rows); }
    row.start = day;
    return writeIndex(rows);
  }

  /** Both ends of a span, together — see the header. Either end may be null to
      clear it, both are validated before anything is written, and a start after
      its target is refused outright rather than silently swapped: a drag that
      crossed over is a gesture whose meaning the code cannot guess, and the
      caller that made it is the one holding the pointer. */
  function mdProjectSetSpan(id: unknown, start: unknown, target: unknown): boolean {
    const clearStart = start == null || start === '';
    const clearTarget = target == null || target === '';
    if (!clearStart && !realDay(start)) return false;
    if (!clearTarget && !realDay(target)) return false;
    if (!clearStart && !clearTarget && (start as string) > (target as string)) return false;

    if (builtinById(id)) {
      const home = homeDoc(id as string) || {};
      return g.mdWrite(K_PROJECT + id, {
        ...home,
        start: clearStart ? null : start,
        target: clearTarget ? null : target,
        at: Date.now(),
      });
    }
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (clearStart) delete row.start; else row.start = start as string;
    if (clearTarget) delete row.target; else row.target = target as string;
    return writeIndex(rows);
  }

  /** Where this project stands, in the user's own word — or null to stop
      saying, which puts a built-in back to what the app shipped and leaves an
      ordinary project saying nothing.

      ONLY EVER CALLED BY A PERSON. Nothing derives a status from a count, and
      the 40-day reading (L-4) is a reading, not a promotion: the ladder is the
      user's to climb. A built-in's status lands on its home document beside its
      target, for the reason the header gives — it has no index row. */
  function mdProjectSetStatus(id: unknown, status: unknown): boolean {
    /* "ONLY EVER CALLED BY A PERSON" above was a rule the code stated and could
       not enforce, and an agent is exactly the caller it was written against.
       Asked by late binding, so a build without md-organizer-door.ts behaves as it
       always has — and the refusal is silent-false rather than a throw, because
       an Organizer sweep must not die on a rung it was never allowed to climb. */
    if (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent') return false;
    const clear = status == null || status === '';
    if (!clear && (typeof status !== 'string' || !knownStatus(id, status))) return false;
    /* Being told to stand in Inbox is being told to stand where absence already
       stands, so nothing is stored. That keeps the positional rule working — a
       project left alone follows a renamed or reordered first Backlog status
       rather than being pinned to whatever it was called on the day — and it is
       why a project only acquires a stored word when it is MOVED OUT of Inbox. */
    const inbox = clear ? null : (g.mdWorkflowInboxOf
      ? g.mdWorkflowInboxOf(g.mdWorkflowFor('project', id).id) : null);
    const home = clear || (inbox && inbox.id === status);
    if (builtinById(id)) return builtinSet(id as string, 'status', home ? null : status);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (home) delete row.status;
    else row.status = status as MdProjectStatus;
    return writeIndex(rows);
  }

  /** How hard this project pushes, in the user's own word — or null to stop
      saying, which is a real answer and the one every project already gives.

      Line for line the status setter, and deliberately so: the two are the same
      species of fact, and the day they stop agreeing about who may write one is
      the day the rule below quietly stops applying to half of them. */
  function mdProjectSetPriority(id: unknown, priority: unknown): boolean {
    /* An agent must not decide what matters to somebody. Same door, same late
       binding, same silent false as the status — an Organizer sweep must not
       die on a rung it was never allowed to climb. */
    if (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent') return false;
    const clear = priority == null || priority === '';
    if (!clear && (typeof priority !== 'string'
      || PRIORITIES.indexOf(priority as MdProjectPriority) === -1)) return false;
    if (builtinById(id)) return builtinSet(id as string, 'priority', clear ? null : priority);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (clear) delete row.priority;
    else row.priority = priority as MdProjectPriority;
    return writeIndex(rows);
  }

  /* ── what a project opens with ───────────────────────────────────────────
     HEADINGS AND NOTHING ELSE, in this file's own words (see the top: the
     vision, the why, the milestones). No prompting line under any of them —
     that is the greyed hint md-projects.jsx refuses, and worse, a prompt left
     untyped is stored as though the writer had said it.

     This is seeded into the editor when a project has no writing yet, and is
     not saved until the first keystroke, so a project opened and abandoned
     stays empty rather than syncing headings to every device. */
  /** What a project's target is when nobody says otherwise.

      Published for one reason: the creation sheet must be able to SHOW the
      forty days rather than let them happen quietly behind a blank field. A
      pill reading nothing beside a project that lands with a target is the pill
      telling a small lie, and the alternative — the sheet doing this arithmetic
      itself — is two answers to one question. */
  function mdProjectDefaultTarget(): string { return dayKeyIn(MD_PROJECT_TARGET_DAYS); }

  function mdProjectTemplate(): string {
    return '## The vision\n\n\n## The why\n\n\n## Milestones\n\n';
  }

  /** The whole opening text of a project's main record: the name as the TITLE
      LINE, then the template under it.

      Line one is the title everywhere in this app, so a project's name belongs
      there rather than in a field beside the writing — which is what makes
      renaming a project the same gesture as titling any other record.

      NO `# ` MARKER on it. The editor styles the first line as the title by its
      POSITION, not by a marker (md-markdown.jsx's firstLineTitle), so a hash
      here is not syntax — it is a stray character sitting in the writer's own
      title, exactly as it would be on any other record. */
  function mdProjectSeedText(name: unknown): string {
    const clean = (typeof name === 'string' ? name : '').trim();
    return (clean ? clean + '\n\n' : '') + mdProjectTemplate();
  }

  function mdProjectRename(id: unknown, name: unknown): boolean {
    if (adminHeld(id)) return false;          // not yours to rename
    if (BUILTIN_IDS.has(id as string)) {
      /* A built-in has no index row to rename, so the name lands on its home
         document as an overlay. An empty one is a rename back to what ManDana
         SHIPS — never a null. A null would leave the search index with no title
         to hold, which is the exact bug this whole change exists to fix, and
         mdBuiltinsMaterialise would not repair it because present-and-null
         counts as present. See tests/builtin-identity.test.js. */
      const shipped = BUILTINS.find((row) => row.id === id);
      const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
      return builtinSet(id as string, 'name', clean || (shipped ? shipped.name : null));
    }
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    const clean = (typeof name === 'string' ? name : '').trim().slice(0, NAME_MAX);
    if (!clean) return false;
    row.name = clean;
    return writeIndex(rows);
  }

  /** Move one to another part of life. Every record defers to mdProjectDomainFor,
      so this single write moves them all and none of them is rewritten. */
  function mdProjectSetDomain(id: unknown, domainId: unknown): boolean {
    if (adminHeld(id)) return false;          // a built-in's domain is not a preference
    if (!g.mdDomainById(domainId)) return false;
    if (BUILTIN_IDS.has(id as string)) return builtinSet(id as string, 'domainId', domainId);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (row.pursuitId && g.mdPursuitById && g.mdPursuitById(row.pursuitId)) return false;
    row.domainId = domainId as string;
    return writeIndex(rows);
  }

  /** Join a finite project to enduring striving. Clearing the pursuit keeps the
      inherited domain as the project's fallback, so no project becomes homeless. */
  function mdProjectSetPursuit(id: unknown, pursuitId: unknown): boolean {
    if (adminHeld(id)) return false;
    if (BUILTIN_IDS.has(id as string)) {
      return builtinSet(id as string, 'pursuitId',
        pursuitId == null || pursuitId === '' ? null : pursuitId);
    }
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    if (pursuitId == null || pursuitId === '') {
      const current = row.pursuitId && g.mdPursuitById ? g.mdPursuitById(row.pursuitId) : null;
      if (current) row.domainId = current.domainId;
      delete row.pursuitId;
      return writeIndex(rows);
    }
    const pursuit = g.mdPursuitById ? g.mdPursuitById(pursuitId) : null;
    if (!pursuit) return false;
    row.pursuitId = pursuit.id;
    row.domainId = pursuit.domainId;
    return writeIndex(rows);
  }

  /* ── the same three changes, with the refusal said out loud ──────────────
     See MdProjectRefusal above for why. These are DOORS ONTO THE SETTERS, not
     second copies of them: each one asks the questions whose answer the setter
     collapses into false, then hands the work to the setter itself. So there is
     still exactly one place that writes a status, and no chance of the two
     drifting into disagreeing about what is allowed.

     A write that gets past every check and still fails did so in storage, and
     the health record is the only thing that knows whether that was a full
     device or something else — the same reading md-database.ts:1321 takes. */
  function writeRefusal(): MdProjectResult {
    const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
    return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'invalid' };
  }

  const OK: MdProjectResult = { ok: true };

  /** A project's name, with the three ways it can fail told apart: a built-in
      is not yours to rename, an empty name is not a name, and everything else
      that goes wrong went wrong in storage. The record screen's title field
      restores a refused draft, so it has to know it WAS refused. */
  function mdProjectTryRename(id: unknown, name: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    /* An empty name is not a name — EXCEPT on a built-in, where it is the admin
       putting back the name the app ships. Clearing an overlay is a real
       instruction, not an absent one. */
    if (!(typeof name === 'string' ? name : '').trim() && !BUILTIN_IDS.has(id as string)) {
      return { ok: false, reason: 'invalid' };
    }
    return mdProjectRename(id, name) ? OK : writeRefusal();
  }

  /** Done with, or picked back up. Closing is not a verdict and not a deletion:
      the project keeps everything it has and simply stops being in the way. A
      built-in cannot be closed — the app ships it, so its life is not yours to
      end. */
  function mdProjectTrySetClosed(id: unknown, closed: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    return mdProjectSetClosed(id, !!closed) ? OK : writeRefusal();
  }

  /** Gone, with its writing. Records in days are deliberately left alone — see
      mdProjectDelete. */
  function mdProjectTryDelete(id: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    return mdProjectDelete(id) ? OK : writeRefusal();
  }

  /** Put back a built-in the admin removed. Only a built-in can be restored —
      an ordinary project's delete takes its writing with it and there is
      nothing left to bring back, which is exactly the difference worth keeping
      visible in two separate verbs. */
  function mdProjectTryRestore(id: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!BUILTIN_IDS.has(id as string)) return { ok: false, reason: 'missing' };
    if (!builtinRemoved(id as string)) return OK;      // already here; nothing to do
    return builtinSet(id as string, 'deleted', null) ? OK : writeRefusal();
  }

  /** Where a project stands, refusing in the user's language rather than in a
      boolean. A BUILT-IN MAY TAKE ONE: its status lands on its home document,
      which is the whole point of BUILTIN_FIELDS. */
  function mdProjectTrySetStatus(id: unknown, status: unknown): MdProjectResult {
    if (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent') {
      return { ok: false, reason: 'agent' };
    }
    const clear = status == null || status === '';
    if (!clear && (typeof status !== 'string' || !knownStatus(id, status))) {
      return { ok: false, reason: 'invalid' };
    }
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    return mdProjectSetStatus(id, status) ? OK : writeRefusal();
  }

  /** How hard it pushes, refusing in the user's language. A BUILT-IN MAY TAKE
      ONE, by the same BUILTIN_FIELDS route its status travels. */
  function mdProjectTrySetPriority(id: unknown, priority: unknown): MdProjectResult {
    if (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent') {
      return { ok: false, reason: 'agent' };
    }
    const clear = priority == null || priority === '';
    if (!clear && (typeof priority !== 'string'
      || PRIORITIES.indexOf(priority as MdProjectPriority) === -1)) return { ok: false, reason: 'invalid' };
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    return mdProjectSetPriority(id, priority) ? OK : writeRefusal();
  }

  /** Both ends of a span, with the refusal named. The timeline is the caller:
      a bar dragged off the calendar or across its own other end has to be told
      apart from a device that ran out of room mid-drag. */
  function mdProjectTrySetSpan(id: unknown, start: unknown, target: unknown): MdProjectResult {
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    const clearStart = start == null || start === '';
    const clearTarget = target == null || target === '';
    if ((!clearStart && !realDay(start)) || (!clearTarget && !realDay(target))) {
      return { ok: false, reason: 'invalid' };
    }
    if (!clearStart && !clearTarget && (start as string) > (target as string)) {
      return { ok: false, reason: 'invalid' };
    }
    return mdProjectSetSpan(id, start, target) ? OK : writeRefusal();
  }

  /** Join a project to a pursuit, or set it loose. */
  function mdProjectTrySetPursuit(id: unknown, pursuitId: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!mdProjectById(id)) return { ok: false, reason: 'missing' };
    const clear = pursuitId == null || pursuitId === '';
    if (!clear && !(g.mdPursuitById && g.mdPursuitById(pursuitId))) return { ok: false, reason: 'invalid' };
    return mdProjectSetPursuit(id, pursuitId) ? OK : writeRefusal();
  }

  /** Move a project to another part of life — which it may not do while a
      pursuit holds it, because the domain is then the PURSUIT'S to give and
      changing it here would put the two in disagreement. */
  function mdProjectTrySetDomain(id: unknown, domainId: unknown): MdProjectResult {
    if (adminHeld(id)) return { ok: false, reason: 'admin' };
    if (!g.mdDomainById(domainId)) return { ok: false, reason: 'invalid' };
    const row = mdProjectById(id);
    if (!row) return { ok: false, reason: 'missing' };
    if (row.pursuitId && g.mdPursuitById && g.mdPursuitById(row.pursuitId)) {
      return { ok: false, reason: 'has_pursuit' };
    }
    return mdProjectSetDomain(id, domainId) ? OK : writeRefusal();
  }

  /** End one, quietly. Its records stay exactly where they are, in their days. */
  function mdProjectSetClosed(id: unknown, closed: boolean): boolean {
    if (adminHeld(id)) return false;
    // `null` rather than false, so a reopened built-in holds no opinion at all
    // and reads exactly as one that was never closed.
    if (BUILTIN_IDS.has(id as string)) return builtinSet(id as string, 'closed', closed ? true : null);
    const rows = readIndex();
    const row = rows.find((p) => p.id === id);
    if (!row) return false;
    row.closed = !!closed;
    return writeIndex(rows);
  }

  /** Remove the project and its writing. Records in days are never touched — the
      worst this can do is leave some pointing at a project that is gone, which
      reads everywhere as "no project". */
  function mdProjectDelete(id: unknown): boolean {
    if (adminHeld(id)) return false;
    // A built-in leaves by tombstone; see builtinRemoved.
    if (BUILTIN_IDS.has(id as string)) return builtinSet(id as string, 'deleted', true);
    const rows = readIndex();
    if (!rows.some((p) => p.id === id)) return false;
    writeIndex(rows.filter((p) => p.id !== id));
    g.mdRemove(K_PROJECT + id);
    return true;
  }

  /* ── the project's own writing ───────────────────────────────────────────── */

  function mdProjectTextRead(id: unknown): string {
    if (!mdProjectById(id)) return '';
    const raw = g.mdRead(K_PROJECT + id, null);
    return raw && typeof raw === 'object' && typeof raw.text === 'string' ? raw.text : '';
  }

  /** Where this project's writing is kept, and when it was last written — `at`
      is 0 when nothing is written there.

      A BUILT-IN PROJECT HAS NO INDEX ROW, so this document is the only place a
      user-set field or a paragraph about it can live, and the only clock it
      has. The Librarian's X-ray asks here rather than spelling the key out in
      another file; see mdDomainWriting for the whole argument. */
  function mdProjectWriting(id: unknown): { key: string; at: number } {
    if (!mdProjectById(id)) return { key: '', at: 0 };
    const raw = homeDoc(id as string);
    const at = raw && typeof raw.at === 'number' && Number.isFinite(raw.at) ? raw.at : 0;
    return { key: K_PROJECT + id, at };
  }

  function mdProjectLinksRead(id: unknown): unknown[] {
    if (!mdProjectById(id)) return [];
    const raw = g.mdRead(K_PROJECT + id, null);
    if (!raw || typeof raw !== 'object') return [];
    return g.mdWikiBindings ? g.mdWikiBindings(raw.links, raw.text) : [];
  }

  function mdProjectTextWrite(id: unknown, text: unknown, links?: unknown): boolean {
    if (!mdProjectById(id)) return false;
    const body = typeof text === 'string' ? text : '';
    if (!body.trim()) {
      // Emptying the writing must not take the user-set fields with it: the home
      // document is the built-in's only place to keep them.
      const kept = homeFields(homeDoc(id as string));
      if (!kept) return g.mdRemove(K_PROJECT + id);
      return g.mdWrite(K_PROJECT + id, { ...kept, at: Date.now() });
    }
    const previous = g.mdRead(K_PROJECT + id, null);
    const carried = links === undefined && g.mdWikiReconcile && previous && typeof previous === 'object'
      ? g.mdWikiReconcile(previous.text, body, previous.links) : links;
    const clean = g.mdWikiBindings ? g.mdWikiBindings(carried, body) : [];
    return g.mdWrite(K_PROJECT + id, { ...(homeFields(previous) || {}),
      text: body, ...(clean.length ? { links: clean } : {}), at: Date.now() });
  }

  /* ── where a record stands ───────────────────────────────────────────────
     A project could say where it stood since the field was written; the work
     INSIDE it could not, which meant the only thing a board could ever hold was
     the container. WF-6 gives a record the same one word, and puts it in the
     one place that costs nothing anywhere else:

     A MAP KEYED BY RECORD UID, ON THE PROJECT'S OWN HOME DOCUMENT. Not in the
     day. `md:events:{day}` is the single shape that merges item by item and
     carries the sync deletion trap, and this phase stays out of it entirely —
     so a record moved across a board leaves its day document byte-identical.

     And not on the backlog row either, which is the subtler half. Scheduling a
     backlog record spreads its row into a day item and deletes the row
     (md-app.jsx:710): a `status` field sitting on that row would ride into the
     day document on the spread, breaking the rule above by accident, in a file
     that never mentions statuses. Keeping the word under the UID means the uid
     and the pid both survive that move, so the word survives it too — with no
     code in the scheduling path and nothing to remember.

     ABSENCE IS INBOX, one rung further down (WF-2). Nothing in existence is
     rewritten, and no composer, day card or editor gains a control: writing in
     a day never asks you where the thing you just wrote stands.

     A RECORD WITH NO PROJECT IS REFUSED rather than quietly dropped. There is
     nowhere beside a project to write it. It still READS as its domain
     workflow's Inbox — everything is somewhere, including the thought nobody
     has filed — it simply cannot be moved out of Inbox until it is filed. */

  /** Every record there is, from both of its homes plus the backlog. One walk,
      because a status now belongs to a RECORD rather than to the folder it is
      sitting in, and every question about who is standing where has to ask all
      of them. */
  function everyRecord(): Record<string, unknown>[] {
    const out: Record<string, unknown>[] = [];
    const seen = new Set<string>();
    /* THE DAY COMES WITH IT. A day record's day is the DOCUMENT it lives in and
       never a field on the row, so a walk that throws the key away leaves every
       caller asking about a tick under the wrong day — the day the record was
       written rather than the day somebody put it on. It is attached to a
       shallow copy, because these rows are the stored objects themselves and a
       walk is not allowed to write on them. */
    const take = (rec: any, day?: string) => {
      if (!rec || typeof rec !== 'object') return;
      if (typeof rec.uid !== 'string' || !rec.uid || seen.has(rec.uid)) return;
      seen.add(rec.uid);
      out.push(day ? { ...rec, scheduled: day } : rec);
    };
    for (const kind of ['events', 'records']) {
      for (const day of (g.mdStoreKeysFor ? g.mdStoreKeysFor(kind) : [])) {
        const list = g.mdStoreRead ? g.mdStoreRead(kind, day) : [];
        if (Array.isArray(list)) for (const rec of list) take(rec, day);
      }
    }
    for (const rec of (g.mdProjectRecordIndex ? g.mdProjectRecordIndex() : [])) take(rec);
    return out;
  }

  /** The word one record is WEARING, before any workflow is asked about it.

      Two places, and the order is the migration. `md:recordStatus` is where
      this build writes (WR-2). The row's own `status` is where the SHIPPED
      build wrote, and records carrying it exist on real devices — so it is
      read, never rewritten, and it loses to anything said since. */
  function storedWordOf(record: unknown): string | null {
    const row = record && typeof record === 'object' ? record as Record<string, unknown> : {};
    const uid = typeof row.uid === 'string' ? row.uid : '';
    if (!uid) return null;
    const said = g.mdRecordStatusStored ? g.mdRecordStatusStored(uid) : null;
    if (said) return said;
    return typeof row.status === 'string' && row.status ? row.status : null;
  }

  /** Every word this project's records are wearing, by uid. The Statuses page
      counts with it, and the delete sheet says how many it is about to move. */
  function mdProjectRecordStatuses(projectId: unknown): Record<string, string> {
    if (typeof projectId !== 'string' || !mdProjectById(projectId)) return {};
    const out: Record<string, string> = {};
    for (const rec of everyRecord()) {
      if (rec.pid !== projectId) continue;
      const word = storedWordOf(rec);
      if (word) out[rec.uid as string] = word;
    }
    return out;
  }

  /** Move a record, or clear it with null. Picking Inbox IS the clear, exactly
      as it is for a project: storing the place you are already standing would
      make the commonest state the most-written one, and would pin the record to
      a word that stops meaning anything the moment the workflow changes. */
  function mdRecordSetStatus(record: unknown, status: unknown): boolean {
    const row = record && typeof record === 'object' ? record as Record<string, unknown> : null;
    if (!row || typeof row.uid !== 'string' || !row.uid) return false;
    const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(record) : null;
    const inbox = flow && g.mdWorkflowInboxOf ? g.mdWorkflowInboxOf(flow.id) : null;
    const home = !!(inbox && inbox.id === status);
    const clear = status == null || status === '' || home;
    if (!clear && typeof status !== 'string') return false;
    /* THE CLEAR HAS TO BE SPOKEN when the row underneath still carries the word
       the shipped build wrote there. Saying nothing would let that word rise
       again the moment this map forgot — so Inbox is written out loud, and only
       a record with nothing underneath gets the quiet clear the common case
       deserves. */
    const shadowed = typeof row.status === 'string' && !!row.status;
    const write = clear ? (shadowed && inbox ? inbox.id : null) : status;
    return g.mdRecordStatusSet ? g.mdRecordStatusSet(row.uid, write) : false;
  }

  /** Is this record ticked off in the day it belongs to?

      `completed` is the flat map md-app holds in React state, and it is passed
      in wherever there is one, because that state is AHEAD of storage: the
      effect that flushes it runs after the render that asks this. Without a
      map — the Statuses page counting, a test — the day's own document answers,
      which is the same fact one flush later. */
  function tickedOn(day: string | null, uid: string, completed: unknown): boolean {
    if (!day || !uid) return false;
    if (completed && typeof completed === 'object') {
      return !!(completed as Record<string, unknown>)[`${day}|${uid}`];
    }
    const map = g.mdStoreRead ? g.mdStoreRead('completed', day) : null;
    return !!(map && typeof map === 'object' && map[uid]);
  }

  /** The status a record is standing on, resolved through the workflow it reads
      through — never null while a workflow has a status in it.

      TWO FACTS, ONE ANSWER (WR-3). The word is what was stored; the tick is
      what the day says. They are reconciled here, once, so that a card in a
      Completed column and a line ticked in somebody's morning can never
      disagree:

      • ticked, and the stored word is already a Completed one — that word. A
        workflow with two Completed statuses says more with its own word than
        the tick does, so the tick does not overwrite it.
      • ticked, and it is not — the workflow's FINISH. Nothing is stored to make
        that true, which is exactly what lets unticking put the card back where
        it came from.
      • not ticked, and the stored word is a Completed one — the word still
        stands. Only the day's own gesture lifts it, and it lifts the word with
        it (`mdRecordTryTick`), so this state is what a record from the shipped
        build looks like on arrival rather than a disagreement.
      • not ticked, and it is not — the word, as it always was.

      The STORED word is not rewritten when the workflow changes under it. A
      vocabulary swapped back must find its records where it left them, so a
      word the current workflow has never heard of reads as that workflow's
      Inbox and waits, rather than being cleared on somebody's behalf. */
  function mdRecordStatusOf(record: unknown, scheduled?: unknown,
    completed?: unknown): MdWorkflowStatus | null {
    const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(record) : null;
    if (!flow || !g.mdWorkflowResolveStatus) return null;
    const stood = g.mdWorkflowResolveStatus(flow.id, storedWordOf(record));
    if (stood && stood.category === 'completed') return stood;
    const row = record && typeof record === 'object' ? record as Record<string, unknown> : {};
    const uid = typeof row.uid === 'string' ? row.uid : '';
    const on = g.mdRecordTickDay ? g.mdRecordTickDay(record, scheduled) : null;
    if (!tickedOn(on, uid, completed)) return stood;
    return (g.mdWorkflowFinishOf ? g.mdWorkflowFinishOf(flow.id) : null) || stood;
  }

  /** Put a tick in a day's own document, or take one out. Returns true when
      there was nothing to do, so a caller can tell "already so" from "the
      device is full" by asking mdStorageLastError. */
  function writeTick(day: string | null, uid: string, on: boolean): boolean {
    if (!day || !uid || !g.mdStoreRead || !g.mdStoreWrite) return true;
    const held = g.mdStoreRead('completed', day);
    const map = (held && typeof held === 'object' ? held : {}) as Record<string, unknown>;
    if (!!map[uid] === on) return true;
    const next = { ...map };
    if (on) next[uid] = true; else delete next[uid];
    return g.mdStoreWrite('completed', day, next);
  }

  /** The same move with the wall named.

      `no_project` is retired (Marwan, 2026-08-18). EVERY record has a status,
      including the ones belonging to nothing at all: an unfiled thought reads
      through its domain, and one with no domain either reads through the
      default workflow. There is no longer anywhere a record can be where its
      status has nowhere to live, so there is no longer a wall to name. The
      word is checked against the workflow the RECORD resolves to rather than
      the one its project does, which for a filed record is the same answer. */
  function mdRecordTrySetStatus(record: unknown, status: unknown,
    scheduled?: unknown): MdProjectResult {
    if (typeof g.mdOrganizerActor === 'function' && g.mdOrganizerActor() === 'agent') {
      return { ok: false, reason: 'agent' };
    }
    const row = record && typeof record === 'object' ? record as Record<string, unknown> : null;
    if (!row || typeof row.uid !== 'string' || !row.uid) return { ok: false, reason: 'missing' };
    const clear = status == null || status === '';
    const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(record) : null;
    const known = !!(flow && g.mdWorkflowStatusById && g.mdWorkflowStatusById(flow.id, status));
    if (!clear && (typeof status !== 'string' || !known)) {
      return { ok: false, reason: 'invalid' };
    }
    /* md-record.ts owns the rule about what a move writes; this door only
       carries it out. Two copies of that decision is how a board and a day
       start disagreeing. Clearing says nothing about ticks — the day's own
       gesture is the only thing that lifts one. */
    const next = clear ? { status: null }
      : (g.mdRecordStatusNext ? g.mdRecordStatusNext(record, scheduled, status) : null);
    if (!next) return { ok: false, reason: 'invalid' };

    /* THE WORD. `status` absent means the move said nothing about it — moving a
       card to the workflow's finish is the tick alone, and the word underneath
       is left exactly where it was so an untick has somewhere to put the card
       back to. */
    if ('status' in next && !mdRecordSetStatus(record, next.status)) return writeRefusal();

    /* THE TICK, which is the other half of the same move. A card dropped into
       Completed is ticked off in its day here rather than left for somebody to
       notice — a card in Done still unticked in a morning is the bug this whole
       arrangement exists to make impossible. */
    if (typeof next.tick !== 'boolean') return OK;
    const on = g.mdRecordTickDay ? g.mdRecordTickDay(record, scheduled) : null;
    return writeTick(on, row.uid, next.tick) ? OK : writeRefusal();
  }

  /** The day's own gesture, through the same door.

      Ticking needs nothing stored — the read derives the workflow's finish from
      the tick alone, which is what lets unticking put the card back where it
      came from. LIFTING one has to lift the word with it, or a record wearing a
      Completed word would stay in that column while the morning says it is not
      done.

      IT TAKES A UID, and looks the record up only when there is something to
      lift. This runs inside a tap, and `md:recordStatus` is one small map: a
      record that has never been moved has nothing stored, so the common tick
      costs a single read and never walks the days. A uid that names no record
      at all is a prayer or a block, whose day map is the whole of what it has;
      the door says so rather than refusing.

      The word the SHIPPED build left on a row is deliberately not lifted here.
      It is not in this map, so finding it would cost the walk on every tap, and
      the first time anybody moves that record it is rewritten into the map
      where the next untick will find it. */
  function mdRecordTryTick(uid: unknown, ticked: unknown): MdProjectResult {
    if (typeof uid !== 'string' || !uid) return { ok: false, reason: 'missing' };
    if (ticked) return OK;
    const said = g.mdRecordStatusStored ? g.mdRecordStatusStored(uid) : null;
    if (!said) return OK;
    const record = everyRecord().find((row) => row.uid === uid);
    if (!record) return OK;
    const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(record) : null;
    const stood = flow && g.mdWorkflowResolveStatus
      ? g.mdWorkflowResolveStatus(flow.id, said) : null;
    if (!stood || stood.category !== 'completed') return OK;
    return mdRecordSetStatus(record, null) ? OK : writeRefusal();
  }

  /** How many things stand on each status of one workflow — `{ projects, records }`
      by status id. The Statuses page prints it under every row ("2 projects ·
      14 records") and the delete sheet prints it again to say what it is about
      to move, which is why one function answers both: a consequence shown
      before a click and a consequence stated during it must be the same number.

      COUNTS, NOT SCORES. Nine projects under Completed is evidence of nine
      projects, the same way IQAMA's forty days is a count of forty days. The
      law this app holds is against being pleased with somebody, not against
      arithmetic — and the rule that used to forbid percentages here was struck
      on 2026-08-18 (WF-2), so nothing is being tiptoed around.

      It counts what RESOLVES here, not what is stored here: a project that has
      never said stands in Inbox and is counted in Inbox, because that is where
      it is. Everything is somewhere, so everything is in exactly one row. */
  function mdProjectStatusUsage(workflowId: unknown):
    Record<string, { projects: number; records: number }> {
    const out: Record<string, { projects: number; records: number }> = {};
    const bump = (id: string | null | undefined, field: 'projects' | 'records') => {
      if (!id) return;
      if (!out[id]) out[id] = { projects: 0, records: 0 };
      out[id][field] += 1;
    };

    for (const project of mdProjectIndex()) {
      if (!g.mdWorkflowFor || g.mdWorkflowFor('project', project.id).id !== workflowId) continue;
      const stood = mdProjectStatusOf(project.id);
      bump(stood ? stood.id : null, 'projects');
    }

    /* Every record there is, day-anchored and backlog alike. A record with no
       project reads through its DOMAIN's workflow (WF-4), so it belongs in this
       count too — that is what makes "what is sitting in my Inbox across all
       ten domains" a question with an honest answer. */
    for (const rec of everyRecord()) {
      const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(rec) : null;
      if (!flow || flow.id !== workflowId) continue;
      const stood = mdRecordStatusOf(rec, rec.scheduled);
      bump(stood ? stood.id : null, 'records');
    }
    return out;
  }

  /** Everything that STORES one word, moved to another word, in one storage
      task — so ⌘Z takes back the status and every holder together rather than
      leaving fourteen records reassigned to a status that no longer exists.

      HOLDERS, NOT STANDERS. A project sitting in Inbox has written nothing
      down; there is nothing to move, and after the delete it falls to whatever
      the workflow's first Backlog status has become. Rewriting it would be the
      app inventing a word on somebody's behalf and calling it a migration.

      IT ROLLS BACK. Every document is read before it is touched and put back
      exactly as it was if any write fails — a device that fills up halfway
      through must not leave half a vocabulary pointing at a status the next
      write is about to delete. The caller gets `undo()` too, because the
      status's own removal is a second write it makes afterwards, and a failure
      there has to unwind this. */
  function mdProjectStatusMoveAll(workflowId: unknown, from: unknown, to: unknown):
    { ok: boolean; projects: number; records: number; reason?: MdProjectRefusal; undo: () => void } {
    const seen = new Map<string, unknown>();
    const keep = (key: string) => { if (!seen.has(key)) seen.set(key, g.mdRead(key, null)); };
    const undo = () => {
      for (const [key, raw] of seen) { if (raw == null) g.mdRemove(key); else g.mdWrite(key, raw); }
    };
    const mine = (id: string) =>
      !!g.mdWorkflowFor && g.mdWorkflowFor('project', id).id === workflowId;
    const inbox = g.mdWorkflowInboxOf ? g.mdWorkflowInboxOf(workflowId) : null;
    const landsHome = !!(inbox && inbox.id === to);
    let projects = 0;
    let records = 0;
    const fail = (): { ok: boolean; projects: number; records: number;
      reason: MdProjectRefusal; undo: () => void } => {
      undo();
      const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
      return { ok: false, projects: 0, records: 0,
        reason: fault && fault.quota ? 'storage_full' : 'invalid', undo };
    };

    for (const project of mdProjectIndex()) {
      if (project.status !== from || !mine(project.id)) continue;
      keep(K_INDEX);
      keep(K_PROJECT + project.id);
      if (!mdProjectSetStatus(project.id, to)) return fail();
      projects += 1;
    }

    /* ONE WALK AND ONE WRITE, because the word lives in one flat document now
       (WR-2). A record is mine if the record itself reads through this
       workflow — which catches the unfiled ones too, and those are exactly the
       records the per-project version could never see. */
    const patch: Record<string, string | null> = {};
    for (const rec of everyRecord()) {
      const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(rec) : null;
      if (!flow || flow.id !== workflowId) continue;
      if (storedWordOf(rec) !== from) continue;
      /* Landing in Inbox is landing in the place absence already means, so the
         honest write is to stop saying anything — unless the row underneath
         carries the shipped build's word, which has to be spoken over. */
      const shadowed = typeof rec.status === 'string' && !!rec.status;
      patch[rec.uid as string] = landsHome ? (shadowed && inbox ? inbox.id : null) : to as string;
      records += 1;
    }
    if (records) {
      keep(g.MD_RECORD_STATUS_KEY);
      if (!g.mdRecordStatusSetMany || !g.mdRecordStatusSetMany(patch)) return fail();
    }
    return { ok: true, projects, records, undo };
  }

  /** How many things would MOVE if one status went — which is not the same
      number as how many stand on it, and the delete sheet must say the one it
      is about to act on. Inbox is where the two part company: everything that
      has never said stands there, and none of it would be rewritten. */
  function mdProjectStatusHolders(workflowId: unknown, statusId: unknown):
    { projects: number; records: number } {
    let projects = 0;
    let records = 0;
    for (const project of mdProjectIndex()) {
      if (!g.mdWorkflowFor || g.mdWorkflowFor('project', project.id).id !== workflowId) continue;
      if (project.status === statusId) projects += 1;
    }
    /* Records are counted from the record side, not the project side: an
       unfiled one reads through its domain and holds a word just the same.

       AND IT COUNTS WHAT RESOLVES HERE, not what is stored here — the same rule
       mdProjectStatusUsage holds, because a consequence shown before a click
       and stated during it have to be the same number. A record ticked in its
       day stands in the workflow's finish with nothing stored, and the sheet
       that says "3 records use Completed" is telling the truth about it. The
       move that follows writes only the stored words; the ticked ones need no
       write, because the tick re-resolves to whatever the finish becomes. */
    for (const rec of everyRecord()) {
      const flow = g.mdWorkflowForRecord ? g.mdWorkflowForRecord(rec) : null;
      if (!flow || flow.id !== workflowId) continue;
      const stood = mdRecordStatusOf(rec, rec.scheduled);
      if (stood && stood.id === statusId) records += 1;
    }
    return { projects, records };
  }

  /* ── grouping ────────────────────────────────────────────────────────────── */

  /** Projects gathered under their domains, in the order md-domain.ts lists
      them. Domains with nothing in them are left out entirely: ten headings with
      six of them empty is a progress bar wearing a disguise. */
  function mdProjectsByDomain(opts?: { open?: boolean }): { domain: any; projects: MdProject[] }[] {
    const all = mdProjectIndex(opts);
    const out: { domain: any; projects: MdProject[] }[] = [];
    for (const domain of g.MD_DOMAINS) {
      const projects = all.filter((p) => p.domainId === domain.id);
      if (projects.length) out.push({ domain, projects });
    }
    return out;
  }

  /** Which domain a record belongs to, given the project it joined.

      A PROJECT OWNS ITS DOMAIN. A record in Bodybuilding is Health, necessarily —
      so a record never states both facts and the two can never drift apart. The
      fallback is what the record said on its own, which is what a record with no
      project keeps using, and what one whose project has been deleted falls
      back to rather than losing its colour entirely. */
  function mdProjectDomainFor(pid: unknown, fallback: unknown): string | null {
    const project = mdProjectById(pid);
    if (project) return project.domainId;
    return typeof fallback === 'string' && fallback ? fallback : null;
  }

  /* ── time spent ──────────────────────────────────────────────────────────── */

  /* Has this record's time already gone by? A record is a block on a day, so "spent"
     means the block has FINISHED — a session running right now is time being
     given, not time given. Counting it early would make the number tick upward
     while you watch it, which is the one thing a calm metric must not do. */
  function hasPassed(day: string, min: unknown, dur: unknown, todayKey: string, nowMin: number): boolean {
    if (day < todayKey) return true;
    if (day > todayKey) return false;
    const start = typeof min === 'number' && Number.isFinite(min) ? min : 0;
    const length = typeof dur === 'number' && Number.isFinite(dur) ? dur : 0;
    return start + length <= nowMin;
  }

  /** What one project's card shows.
   *
   *  Pure — every input is handed in — so the counting can be asserted without a
   *  browser, and so md-app can pass the state it already holds rather than this
   *  module re-reading storage on every render.
   */
  function mdProjectStats(id: unknown, ctx: any): MdProjectStats {
    const events = (ctx && ctx.events) || {};
    const boards = Array.isArray(ctx && ctx.boards) ? ctx.boards : [];
    const completed = (ctx && ctx.completed) || {};
    const todayKey = typeof (ctx && ctx.todayKey) === 'string' ? ctx.todayKey : '';
    const nowMin = typeof (ctx && ctx.nowMin) === 'number' ? ctx.nowMin : 0;

    let minutes = 0;
    let records = 0;

    const backlog = Array.isArray(ctx && ctx.projectRecords) ? ctx.projectRecords : [];
    for (const rec of backlog) if (rec && rec.pid === id) records += 1;

    for (const day of Object.keys(events)) {
      if (!DAY_RE.test(day)) continue;                // never trust a key we did not write
      const list = events[day];
      if (!Array.isArray(list)) continue;
      for (const rec of list) {
        if (!rec || typeof rec !== 'object' || rec.pid !== id) continue;
        /* A record counts as a record wherever it sits. That is deliberately not
           the rule for minutes: "12 records" says how much of this project
           exists, and one written for next Tuesday exists now — but no time has
           been given to it yet. */
        records += 1;
        if (hasPassed(day, rec.min, rec.dur, todayKey, nowMin)) {
          minutes += typeof rec.dur === 'number' && Number.isFinite(rec.dur) && rec.dur > 0 ? rec.dur : 0;
        }
      }
    }

    /* The built-in's own arithmetic. Prayers leave no duration behind — only a
       done mark — so they are counted at the nominal window. Any record joined to
       the project by hand has already been added above and is simply added to. */
    if (id === PRAYERS.id) {
      for (const key of Object.keys(completed)) {
        if (!completed[key]) continue;
        const bar = key.indexOf('|');
        if (bar === -1) continue;
        if (!DAY_RE.test(key.slice(0, bar))) continue;
        if (MD_PRAYER_IDS.indexOf(key.slice(bar + 1)) === -1) continue;
        minutes += MD_PRAYER_NOMINAL_MIN;
      }
    }

    let boardCount = 0;
    for (const b of boards) if (b && typeof b === 'object' &&
      (b.pid === id || b.scopeRef === `project:${id}`)) boardCount += 1;

    return { minutes, records, boards: boardCount };
  }

  /* ── the forty-day reading ───────────────────────────────────────────────
     Days counted inside a window that ends today — NOT a streak. A streak
     resets on a miss, which turns a number into a verdict about the last bad
     day; here a gap costs the gap and nothing more. ManDana never shames, and
     this is where that law is a line of arithmetic rather than a slogan: the
     difference between the two readings IS whether a bad day is held against
     somebody afterwards.

     Pure, like mdProjectStats above, and for the same reason: the counting can
     be asserted without a browser. The context is optional because a formula
     cell computes at view time with nothing in hand — so the marks are read
     off the store when nobody hands them over. */

  /** The first day of the window ending at `todayKey`, inclusive of both ends. */
  function windowStart(todayKey: string): string {
    const end = Date.parse(todayKey + 'T00:00:00Z');
    if (!Number.isFinite(end)) return todayKey;
    return new Date(end - (MD_READING_WINDOW_DAYS - 1) * 86400000).toISOString().slice(0, 10);
  }

  /** What this commitment's number says, or `null` when it has none to say.
   *
   *  Two ways to have none, and both are the honest answer rather than a zero:
   *  the project measures nothing this app can see, or the user is not standing
   *  on that rung — a commitment anywhere but IN PROGRESS is silent, because
   *  putting a number on a rung nobody chose to climb is the app appointing
   *  itself referee. Promotion stays the user's, always.
   *
   *  It asks the SPINE CATEGORY rather than the word `active`, which stopped
   *  being the only name for being underway in Phase 5. The number now appears
   *  for whatever this person calls it and for nothing else.
   */
  function mdProjectReading(id: unknown, ctx?: any): MdProjectReading | null {
    const measures = typeof id === 'string' ? READINGS[id] : undefined;
    if (!measures) return null;
    const project = mdProjectById(id);
    if (!project) return null;
    const status = mdProjectStatusOf(id);
    if (!status || status.category !== 'progress') return null;

    const completed = (ctx && ctx.completed) || (g.mdStoreCompletedFlat ? g.mdStoreCompletedFlat() : {});
    const todayKey = typeof (ctx && ctx.todayKey) === 'string' && DAY_RE.test(ctx.todayKey)
      ? ctx.todayKey
      : (g.mdDayKey ? g.mdDayKey() : new Date().toISOString().slice(0, 10));
    const first = windowStart(todayKey);

    /* One pass over the marks, gathering how many of the five each day in the
       window carries; a day short of one is a day that happened and did not
       count, which is the whole distinction this reading exists to make. */
    const marks: Record<string, number> = {};
    for (const key of Object.keys(completed)) {
      if (!completed[key]) continue;
      const bar = key.indexOf('|');
      if (bar === -1) continue;
      const day = key.slice(0, bar);
      if (!DAY_RE.test(day) || day < first || day > todayKey) continue;
      if (MD_PRAYER_IDS.indexOf(key.slice(bar + 1)) === -1) continue;
      marks[day] = (marks[day] || 0) + 1;
    }
    let days = 0;
    for (const day of Object.keys(marks)) if (marks[day] === MD_PRAYER_IDS.length) days += 1;
    return { measures, days, of: MD_READING_WINDOW_DAYS };
  }

  /* ── what travels ────────────────────────────────────────────────────────
     The list, and one document per project's writing. The BUILT-IN is never
     emitted: it is not stored, so there is nothing to send, and pushing it would
     put a row in the index that readIndex then refuses to read back — the two
     would disagree for ever. Its writing does travel, under its own key, like
     any other project's. */
  function mdProjectSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const rows = readIndex();
    if (rows.length) out.projects = rows;
    for (const key of g.mdStorageKeys(K_PROJECT)) {
      const id = key.slice(K_PROJECT.length);
      if (!/^[a-z0-9]{6,40}$/.test(id)) continue;
      const raw = g.mdRead(key, null);
      if (!raw || typeof raw !== 'object') continue;
      // A home document travels when it holds writing OR when it holds a field
      // somebody set. The second half is what makes a built-in's target reach
      // another device: the built-in has no index row to carry it.
      const written = typeof raw.text === 'string' && raw.text.trim();
      if (!written && !homeFields(raw)) continue;
      out[`project:${id}`] = raw;
    }
    return out;
  }

  /** Apply one pulled document. Newer WRITE wins for a project's record, for the
      same reason md-domain.ts does it that way: documents come back in rev
      order, not the order somebody typed in, and paragraphs about an intention
      are not obviously missing once they are gone. */
  function mdProjectApplyDoc(key: unknown, value: unknown): boolean {
    if (typeof key !== 'string') return false;

    if (key === 'projects') {
      if (value == null) return g.mdRemove(K_INDEX);
      return Array.isArray(value) ? writeIndex(value.filter(isRow)) : false;
    }

    if (key.indexOf(K_PROJECT.slice(3)) !== 0) return false;   // 'project:'
    const id = key.slice('project:'.length);
    if (!/^[a-z0-9]{6,40}$/.test(id) && !BUILTIN_IDS.has(id)) return false;
    const stored = K_PROJECT + id;

    if (value == null) return g.mdRemove(stored);
    if (!value || typeof value !== 'object') return false;
    const incoming = value as { text?: unknown; links?: unknown; at?: unknown };
    const said = homeFields(incoming as Record<string, any>);
    const wrote = typeof incoming.text === 'string';
    // A document that carries only user-set fields is a whole document now —
    // that is how a built-in's target arrives from a device where nothing was
    // written beside it.
    if ((!wrote && !said) || !Number.isFinite(incoming.at)) return false;

    const mine = g.mdRead(stored, null);
    if (mine && typeof mine === 'object' && Number.isFinite(mine.at) && mine.at > (incoming.at as number)) return true;

    // Fields merge rather than replace, ONE AT A TIME: the sender speaks for a
    // field only if it mentions that field, so an older client's silence cannot
    // clear what this device knows, while an explicit null still can. See the
    // header — the whole reason a cleared field is stored as a null instead of
    // being deleted is that absence and "cleared" must not look alike, and a
    // group swapped wholesale would make the null pointless. It also stopped
    // being an academic point at WF-6, when a map of where every record in the
    // project stands joined the group: a device pushing a target it changed
    // would have taken fourteen records' places with it.
    const fields = { ...(homeFields(mine) || {}), ...(said || {}) };
    const body = wrote ? (incoming.text as string) : '';
    if (!body.trim()) {
      if (!Object.keys(fields).length) return g.mdRemove(stored);
      return g.mdWrite(stored, { ...fields, at: incoming.at });
    }
    const links = g.mdWikiBindings ? g.mdWikiBindings(incoming.links, body) : [];
    return g.mdWrite(stored, { ...fields, text: body, ...(links.length ? { links } : {}), at: incoming.at });
  }

  g.MD_PRAYER_NOMINAL_MIN = MD_PRAYER_NOMINAL_MIN;
  g.MD_PROJECT_TARGET_DAYS = MD_PROJECT_TARGET_DAYS;
  g.mdProjectDefaultTarget = mdProjectDefaultTarget;
  g.mdProjectTarget = mdProjectTarget;
  g.mdProjectSetTarget = mdProjectSetTarget;
  g.mdProjectStart = mdProjectStart;
  g.mdProjectSetStart = mdProjectSetStart;
  g.mdProjectSetSpan = mdProjectSetSpan;
  g.mdProjectTemplate = mdProjectTemplate;
  g.mdProjectSeedText = mdProjectSeedText;
  g.mdProjectSetDomain = mdProjectSetDomain;
  g.mdProjectSetPursuit = mdProjectSetPursuit;
  g.mdProjectMaterialiseBuiltins = mdProjectMaterialiseBuiltins;
  g.mdProjectRemovedBuiltins = mdProjectRemovedBuiltins;
  g.mdProjectTryRestore = mdProjectTryRestore;
  g.mdProjectSyncDocs = mdProjectSyncDocs;
  g.mdProjectApplyDoc = mdProjectApplyDoc;
  g.mdProjectSetStatus = mdProjectSetStatus;
  g.mdProjectSetPriority = mdProjectSetPriority;
  g.mdProjectPriority = mdProjectPriority;
  g.mdProjectTryRename = mdProjectTryRename;
  g.mdProjectTrySetClosed = mdProjectTrySetClosed;
  g.mdProjectTryDelete = mdProjectTryDelete;
  g.mdProjectTrySetStatus = mdProjectTrySetStatus;
  g.mdProjectTrySetPriority = mdProjectTrySetPriority;
  g.mdProjectTrySetSpan = mdProjectTrySetSpan;
  g.mdProjectTrySetPursuit = mdProjectTrySetPursuit;
  g.mdProjectTrySetDomain = mdProjectTrySetDomain;
  g.mdProjectStatuses = mdProjectStatuses;
  g.mdProjectRecordStatuses = mdProjectRecordStatuses;
  g.mdProjectStatusUsage = mdProjectStatusUsage;
  g.mdProjectStatusHolders = mdProjectStatusHolders;
  g.mdProjectStatusMoveAll = mdProjectStatusMoveAll;
  g.mdRecordStatusOf = mdRecordStatusOf;
  g.mdRecordSetStatus = mdRecordSetStatus;
  g.mdRecordTrySetStatus = mdRecordTrySetStatus;
  g.mdRecordTryTick = mdRecordTryTick;
  g.mdProjectStatusOf = mdProjectStatusOf;
  g.mdProjectStatusLanding = mdProjectStatusLanding;
  g.MD_PROJECT_PRIORITIES = PRIORITIES;
  g.MD_PROJECT_BUILTIN_ID = PRAYERS.id;              // Prayers, by name, for callers that mean it
  g.MD_PROJECT_BUILTIN_IDS = [...BUILTIN_IDS];
  g.mdProjectIndex = mdProjectIndex;
  g.mdProjectById = mdProjectById;
  g.mdProjectCreate = mdProjectCreate;
  g.mdProjectRename = mdProjectRename;
  g.mdProjectSetClosed = mdProjectSetClosed;
  g.mdProjectDelete = mdProjectDelete;
  g.mdProjectTextRead = mdProjectTextRead;
  g.mdProjectWriting = mdProjectWriting;
  g.mdProjectLinksRead = mdProjectLinksRead;
  g.mdProjectTextWrite = mdProjectTextWrite;
  g.mdProjectsByDomain = mdProjectsByDomain;
  g.mdProjectDomainFor = mdProjectDomainFor;
  g.mdProjectStats = mdProjectStats;
  g.mdProjectReading = mdProjectReading;

  // CommonJS escape hatch, following md-domain.ts and md-board.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_PRAYER_NOMINAL_MIN, mdProjectIndex, mdProjectById, mdProjectCreate,
      mdProjectRename, mdProjectSetClosed, mdProjectDelete, mdProjectTextRead, mdProjectWriting, mdProjectLinksRead, mdProjectTextWrite,
      mdProjectsByDomain, mdProjectStats, mdProjectReading };
  }
})(typeof window !== 'undefined' ? window : globalThis);
